import React, { useState } from "react";
import { PhoneOutlined, GlobalOutlined } from "@ant-design/icons";

import "./CarrierContactBar.css";
import CarrierContactSuggestModal from "./CarrierContactSuggestModal";
import CarrierPlaybookPanel from "./CarrierPlaybookPanel";
import { pmsCarrierLabel } from "../../lib/carrierLabel";

// How this carrier gives up each kind of work — drives the "on portal" vs
// "by phone" hint so an FTC doesn't waste a portal login for call-only data.
const METHOD_LABEL = {
  portal: "On portal",
  call: "By phone",
  call_email: "By phone & email",
};

// tel: links want digits only; keep a leading + for international.
function telHref(phone) {
  const cleaned = String(phone || "").replace(/[^0-9+]/g, "");
  return cleaned ? "tel:" + cleaned : undefined;
}

// Stored portal URLs should carry a scheme, but guard scheme-less values so the
// link never resolves relative to the app origin.
function withScheme(url) {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : "https://" + url;
}

function MethodRow({ label, method }) {
  const text = METHOD_LABEL[method];
  if (!text) return null;
  const isPortal = method === "portal";
  return (
    <div className="carrier-contact-method">
      <span className="carrier-contact-method-k">{label}</span>
      <span className={"carrier-contact-method-v" + (isPortal ? " is-portal" : " is-call")}>{text}</span>
    </div>
  );
}

// Same row shape as MethodRow, but always rendered: an empty identifier shows a
// muted "Not set" so staff can see the office profile still needs filling in
// (Office -> Portal Credentials) rather than wondering why the row is absent.
function IdRow({ label, value }) {
  const text = (value || "").trim();
  return (
    <div className="carrier-contact-method">
      <span className="carrier-contact-method-k">{label}</span>
      {text ? (
        <span className="carrier-contact-id-v" title={text}>{text}</span>
      ) : (
        <span className="carrier-contact-id-v is-unset">Not set</span>
      )}
    </div>
  );
}

/**
 * Carrier contact card for the insurance modal. Given the selected policy, shows
 * the carrier's phone number (as a tel: link), a link to its provider portal,
 * and how breakdowns/estimates are obtained. Renders nothing when the carrier
 * has no contact info on file.
 *
 * Optional AI-call affordance: when this carrier's breakdowns come "by phone"
 * AND the capability matrix allows ai_phone_call for it (graduated carriers
 * only), pass `onAiCall` + `aiCallEnabled` to show the "AI Call" button that
 * dispatches the voice-agent instead of a human dialing the tel: link.
 */
export default function CarrierContactBar({ policy, identity, taskType, onAiCall, aiCallEnabled, aiCallBusy }) {
  const [suggestOpen, setSuggestOpen] = useState(false);

  if (!policy) return null;

  const phone = policy.carrier_phone_number;
  const url = policy.carrier_portal_url;
  const portalName = policy.carrier_portal_name;
  const carrier = policy.carrier_display_name || policy.insurance_name_display || policy.insurance_name;
  const bMethod = policy.carrier_breakdown_method;
  const eMethod = policy.carrier_estimate_method;

  const hasMethods = METHOD_LABEL[bMethod] || METHOD_LABEL[eMethod];
  // The PMS's own name for the carrier, when it names something else ("GMS
  // Insurance Inc" filed under "Telus") — the FTC phones the insurer on the card.
  const pmsLabel = pmsCarrierLabel(carrier, policy.sikka_carrier_name_raw);
  // `identity` counts toward "has something to show": a carrier with no phone,
  // portal or methods still needs the card so the submission IDs are reachable.
  if (!phone && !url && !hasMethods && !identity && !pmsLabel) return null;

  return (
    <div className="carrier-contact-bar">
      <div className="carrier-contact-head">
        <span className="carrier-contact-title">Carrier contact</span>
        {carrier ? <span className="carrier-contact-carrier">{carrier}</span> : null}
      </div>
      {pmsLabel ? (
        <div className="carrier-contact-pms" title="The name the office's practice management system uses for this carrier">
          PMS calls it: <strong>{pmsLabel}</strong>
        </div>
      ) : null}

      <div className="carrier-contact-links">
        {phone ? (
          <a className="carrier-contact-item" href={telHref(phone)}>
            <PhoneOutlined className="carrier-contact-ico" />
            <span className="carrier-contact-text">{phone}</span>
          </a>
        ) : null}
        {url ? (
          <a className="carrier-contact-item" href={withScheme(url)} target="_blank" rel="noopener noreferrer">
            <GlobalOutlined className="carrier-contact-ico" />
            <span className="carrier-contact-text">{portalName || "Provider portal"}</span>
            <span className="carrier-contact-ext" aria-hidden="true">↗</span>
          </a>
        ) : null}
      </div>

      {hasMethods ? (
        <div className="carrier-contact-methods">
          <MethodRow label="Breakdowns" method={bMethod} />
          <MethodRow label="Estimates" method={eMethod} />
        </div>
      ) : null}

      {/* Super admins and FTC only. Renders nothing for anyone else, because the
          endpoint refuses them — the office sees this card unchanged. */}
      <CarrierPlaybookPanel policy={policy} taskType={taskType} />

      {identity ? (
        <div className="carrier-contact-methods">
          {/* Labelled "Office" on purpose: these are the OFFICE's submission
              details (one row per office, shared across every portal and
              carrier), so under a "Carrier contact" heading they would otherwise
              read as carrier-specific and vary with the dropdown, which they
              don't. Edited on Office -> Portal Credentials, where they are
              collected when a portal account is made the default. The doctor
              and CDA / iTrans number are what a carrier asks for on the phone
              alongside the IDs, so they belong on the same card. A dentist's
              OWN numbers, when a per-provider login is selected, appear on the
              stored-login card below instead. */}
          <div className="carrier-contact-subtitle">Office submission details</div>
          <IdRow label="Doctor" value={identity.doctor_name} />
          <IdRow label="Access ID" value={identity.access_id} />
          <IdRow label="License #" value={identity.license_number} />
          <IdRow label="Unique ID" value={identity.unique_id} />
          <IdRow label="CDA / iTrans #" value={identity.cda_itrans_number} />
        </div>
      ) : null}

      {/* Anyone who can see this card can report it wrong — the FTC staff who
          dial these numbers find out first. Files a suggestion for a super-admin
          to accept in the CRM; it never edits the carrier directly. */}
      {policy.carrier_id ? (
        <button
          type="button"
          className="carrier-contact-suggest"
          onClick={function () { setSuggestOpen(true); }}
        >
          Suggest a correction
        </button>
      ) : null}

      <CarrierContactSuggestModal
        open={suggestOpen}
        onClose={function () { setSuggestOpen(false); }}
        policy={policy}
      />

      {aiCallEnabled && onAiCall && (bMethod === "call" || bMethod === "call_email") ? (
        <button
          type="button"
          className="carrier-contact-ai-call"
          disabled={!!aiCallBusy}
          onClick={() => onAiCall()}
          title="A digital assistant calls the carrier, waits on hold, and records the breakdown for review."
        >
          <PhoneOutlined className="carrier-contact-ico" />
          {aiCallBusy ? "Calling carrier..." : "AI Call for breakdown"}
        </button>
      ) : null}
    </div>
  );
}
